/**
 * User-facing messages for SSO error codes returned by the backend via `?sso_error=<code>`.
 *
 * Error codes are set by the SSO gateway/backend and appended as a query
 * parameter when the user is redirected back to the landing page after a
 * failed SSO login attempt.
 *
 * Codes:
 * - `local_user_not_found`    – authenticated via SSO but no matching local account exists.
 * - `local_user_inactive`     – matching local account exists but is disabled.
 * - `local_user_unauthorized` – account exists but has no projects assigned.
 * - `sso_failed`              – generic / unexpected SSO failure.
 */
const SSO_ERROR_MESSAGES: Record<string, string> = {
    local_user_not_found:
        "You don't have an account in ZINA BI. Please contact (support.zina@nokia.com) to request access.",
    local_user_inactive: 'Your account is disabled. Please contact (support.zina@nokia.com).',
    local_user_unauthorized:
        "Your account doesn't have any projects assigned. Please contact (support.zina@nokia.com).",
    sso_failed: 'We could not complete your sign in. Please try again or use the local login.',
};

const DEFAULT_MESSAGE = SSO_ERROR_MESSAGES.sso_failed;

/**
 * Returns a user-facing error message for the given SSO error code.
 *
 * @param code - The `sso_error` query-parameter value from the redirect URL, or null/undefined.
 * @returns A translated message string, or `null` when no error code is present.
 */
export function getSsoErrorMessage(code?: string | null): string | null {
    if (!code) {
        return null;
    }

    return SSO_ERROR_MESSAGES[code] ?? DEFAULT_MESSAGE;
}
